// PlannerGrid — spreadsheet-style monthly work planner. One row per day of the
// selected month; two visible task slots (Work / Assign To / Status) plus a
// "+N more" overflow; Sundays render as a tinted full-width row; inline status
// and assignee editing; native drag-and-drop to reschedule between dates.
import { useMemo, useState } from "react";
import { getDaysInMonth, format, isToday } from "date-fns";
import { Plus, GripVertical, Trash2, Pencil } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PLANNER_STATUSES, PLANNER_PRIORITIES, plannerStatusMeta, normalizePlannerStatus } from "@/services/workPlannerApi";

const pad = (n) => String(n).padStart(2, "0");
const dayKeyOf = (task) => {
  const raw = task.due_date || task.scheduled_date || task.start_date;
  if (!raw) return null;
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : format(d, "yyyy-MM-dd");
};
const memberName = (members, id) => (members.find((m) => m.user_id === id) || {}).name || "";
const priorityDot = (p) => (PLANNER_PRIORITIES.find((x) => x.value === p) || {}).dot || "bg-slate-300";

function StatusSelect({ task, onChange }) {
  const meta = plannerStatusMeta(task.status);
  return (
    <select
      value={normalizePlannerStatus(task.status)}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => { e.stopPropagation(); onChange(task, e.target.value); }}
      data-testid={`planner-status-${task.task_id}`}
      className={`w-full cursor-pointer rounded-full px-2 py-1 text-[11px] font-medium ring-1 ring-inset outline-none ${meta.chip}`}
    >
      {PLANNER_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
    </select>
  );
}

function AssignSelect({ task, members, onChange }) {
  const current = (task.assigned_users || [])[0] || "";
  const extra = (task.assigned_users || []).length - 1;
  return (
    <div className="flex items-center gap-1">
      <select
        value={current}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => { e.stopPropagation(); onChange(task, e.target.value); }}
        data-testid={`planner-assign-${task.task_id}`}
        className="w-full cursor-pointer rounded-md border border-slate-200 bg-white px-1.5 py-1 text-[11px] text-slate-700 outline-none focus:border-indigo-400"
      >
        <option value="">Unassigned</option>
        {members.map((m) => <option key={m.user_id} value={m.user_id}>{m.name}</option>)}
      </select>
      {extra > 0 && <span className="shrink-0 text-[10px] text-slate-400">+{extra}</span>}
    </div>
  );
}

function TaskCell({ task, onOpen, onDragStart }) {
  return (
    <button
      type="button"
      draggable
      onDragStart={(e) => { e.dataTransfer.setData("text/task_id", task.task_id); e.dataTransfer.effectAllowed = "move"; onDragStart?.(); }}
      onClick={() => onOpen(task)}
      title={task.title}
      data-testid={`planner-task-${task.task_id}`}
      className="group flex w-full items-center gap-1.5 rounded-md px-1.5 py-1 text-left text-xs hover:bg-indigo-50"
    >
      <GripVertical className="h-3 w-3 shrink-0 cursor-grab text-slate-300 group-hover:text-slate-400" />
      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${priorityDot(task.priority)}`} />
      <span className={`truncate ${normalizePlannerStatus(task.status) === "completed" ? "text-slate-400 line-through" : "text-slate-800"}`}>{task.title}</span>
    </button>
  );
}

function EmptySlot({ dateStr, onAdd }) {
  return (
    <button type="button" onClick={() => onAdd(dateStr)} className="flex w-full items-center gap-1 rounded-md px-1.5 py-1 text-[11px] text-slate-300 hover:bg-slate-50 hover:text-indigo-500">
      <Plus className="h-3 w-3" /> Add
    </button>
  );
}

export default function PlannerGrid({ year, month, tasks, members = [], onAddTask, onOpenTask, onQuickStatus, onQuickAssign, onReschedule }) {
  const [dragOver, setDragOver] = useState(null);
  const [moreDay, setMoreDay] = useState(null); // { dateStr, label, tasks }

  const byDay = useMemo(() => {
    const map = {};
    for (const t of tasks) { const k = dayKeyOf(t); if (k) (map[k] = map[k] || []).push(t); }
    const rank = { urgent: 0, high: 1, medium: 2, low: 3 };
    for (const k of Object.keys(map)) {
      map[k].sort((a, b) => (rank[a.priority] ?? 2) - (rank[b.priority] ?? 2) || new Date(a.created_at || 0) - new Date(b.created_at || 0));
    }
    return map;
  }, [tasks]);

  const days = useMemo(() => {
    const total = getDaysInMonth(new Date(year, month - 1, 1));
    const out = [];
    for (let d = 1; d <= total; d++) {
      const dt = new Date(year, month - 1, d);
      const dateStr = `${year}-${pad(month)}-${pad(d)}`;
      out.push({ dt, dateStr, dow: dt.getDay(), dayShort: format(dt, "EEE"), label: format(dt, "d-MMM-yyyy"), tasks: byDay[dateStr] || [] });
    }
    return out;
  }, [year, month, byDay]);

  const drop = (dateStr, e) => {
    e.preventDefault(); setDragOver(null);
    const id = e.dataTransfer.getData("text/task_id");
    const t = tasks.find((x) => x.task_id === id);
    if (t && dayKeyOf(t) !== dateStr) onReschedule(t, dateStr);
  };

  const Slot = ({ task, dateStr }) => (
    <>
      <td className="border-r border-slate-100 px-2 py-1.5 align-middle">
        {task ? <TaskCell task={task} onOpen={onOpenTask} /> : <EmptySlot dateStr={dateStr} onAdd={onAddTask} />}
      </td>
      <td className="border-r border-slate-100 px-2 py-1.5 align-middle">
        {task ? <AssignSelect task={task} members={members} onChange={onQuickAssign} /> : <span className="text-slate-200">—</span>}
      </td>
      <td className="border-r border-slate-100 px-2 py-1.5 align-middle">
        {task ? <StatusSelect task={task} onChange={onQuickStatus} /> : <span className="text-slate-200">—</span>}
      </td>
    </>
  );

  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid="planner-grid">
      <table className="w-full min-w-[900px] border-collapse text-sm">
        <thead className="sticky top-0 z-10">
          <tr className="bg-slate-100 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            <th className="border-b border-r border-slate-200 px-2 py-2 text-left" rowSpan={2}>Day</th>
            <th className="border-b border-r border-slate-200 px-2 py-2 text-left" rowSpan={2}>Date</th>
            <th className="border-b border-r border-slate-200 px-2 py-2 text-center" colSpan={3}>Task 1</th>
            <th className="border-b border-r border-slate-200 px-2 py-2 text-center" colSpan={3}>Task 2</th>
            <th className="border-b border-slate-200 px-2 py-2 text-center" rowSpan={2}>Action</th>
          </tr>
          <tr className="bg-slate-50 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
            <th className="border-b border-r border-slate-200 px-2 py-1.5 text-left">Work To Do</th>
            <th className="border-b border-r border-slate-200 px-2 py-1.5 text-left">Assign To</th>
            <th className="border-b border-r border-slate-200 px-2 py-1.5 text-left">Status</th>
            <th className="border-b border-r border-slate-200 px-2 py-1.5 text-left">Work To Do</th>
            <th className="border-b border-r border-slate-200 px-2 py-1.5 text-left">Assign To</th>
            <th className="border-b border-r border-slate-200 px-2 py-1.5 text-left">Status</th>
          </tr>
        </thead>
        <tbody>
          {days.map((day) => {
            const t = day.tasks;
            const isSunday = day.dow === 0;
            const highlight = isToday(day.dt) ? "bg-indigo-50/40" : dragOver === day.dateStr ? "bg-emerald-50" : "";
            const dateCells = (
              <>
                <td className={`border-r border-slate-100 px-2 py-1.5 align-middle font-medium ${isSunday ? "text-rose-500" : "text-slate-500"}`}>{day.dayShort}</td>
                <td className="border-r border-slate-100 px-2 py-1.5 align-middle whitespace-nowrap text-slate-700">
                  {day.label}{isToday(day.dt) && <span className="ml-1 rounded bg-indigo-600 px-1 text-[9px] font-semibold text-white">TODAY</span>}
                </td>
              </>
            );

            if (isSunday && t.length === 0) {
              return (
                <tr key={day.dateStr} className="bg-rose-50/60" onDragOver={(e) => { e.preventDefault(); setDragOver(day.dateStr); }} onDragLeave={() => setDragOver((c) => (c === day.dateStr ? null : c))} onDrop={(e) => drop(day.dateStr, e)} data-testid={`planner-row-${day.dateStr}`}>
                  {dateCells}
                  <td colSpan={6} className="px-2 py-2 text-center text-xs font-semibold uppercase tracking-widest text-rose-400">Sunday</td>
                  <td className="px-2 py-1.5 text-center">
                    <button type="button" onClick={() => onAddTask(day.dateStr)} className="text-slate-300 hover:text-indigo-500" title="Add task"><Plus className="mx-auto h-4 w-4" /></button>
                  </td>
                </tr>
              );
            }

            return (
              <tr key={day.dateStr} className={`${isSunday ? "bg-rose-50/40" : "hover:bg-slate-50/60"} ${highlight}`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(day.dateStr); }}
                onDragLeave={() => setDragOver((c) => (c === day.dateStr ? null : c))}
                onDrop={(e) => drop(day.dateStr, e)}
                data-testid={`planner-row-${day.dateStr}`}>
                {dateCells}
                <Slot task={t[0]} dateStr={day.dateStr} />
                <Slot task={t[1]} dateStr={day.dateStr} />
                <td className="px-2 py-1.5 text-center align-middle">
                  <div className="flex flex-col items-center gap-1">
                    <button type="button" onClick={() => onAddTask(day.dateStr)} data-testid={`planner-add-${day.dateStr}`} className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium text-indigo-600 hover:bg-indigo-50">
                      <Plus className="h-3 w-3" /> Add
                    </button>
                    {t.length > 2 && (
                      <button type="button" onClick={() => setMoreDay({ dateStr: day.dateStr, label: day.label, tasks: t })} className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600 hover:bg-slate-200">
                        +{t.length - 2} more
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* All tasks for a day */}
      <Dialog open={!!moreDay} onOpenChange={(o) => !o && setMoreDay(null)}>
        <DialogContent className="max-w-lg rounded-2xl">
          <DialogHeader><DialogTitle>Tasks · {moreDay?.label}</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] space-y-2 overflow-y-auto">
            {(moreDay?.tasks || []).map((task) => (
              <div key={task.task_id} className="flex items-center gap-2 rounded-lg border border-slate-200 p-2">
                <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${priorityDot(task.priority)}`} />
                <button type="button" onClick={() => { setMoreDay(null); onOpenTask(task); }} className="flex-1 truncate text-left text-sm text-slate-800 hover:text-indigo-600">{task.title}</button>
                <span className="w-28 shrink-0"><AssignSelect task={task} members={members} onChange={onQuickAssign} /></span>
                <span className="w-28 shrink-0"><StatusSelect task={task} onChange={onQuickStatus} /></span>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
